import {
  ArrowLeft,
  LoaderCircle,
  Pause,
  Play,
  RotateCw,
  Shuffle,
  SkipBack,
  SkipForward,
  X,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import { Button } from '../design-system/index.js';
import { QuickCreateButton } from '../features/create/quick-create.js';
import { formatPlaybackTime } from './format-time.js';
import { usePlayer } from './player-context.js';

export function ExpandedPlayer() {
  const player = usePlayer();
  const navigate = useNavigate();

  if (!player.item) {
    return (
      <section className="grid min-h-svh place-items-center bg-bg px-4 text-center text-ink">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Nothing is playing.</h1>
          <Button asChild variant="secondary" className="mt-6">
            <Link to="/catalog">Browse Catalog</Link>
          </Button>
        </div>
      </section>
    );
  }

  const busy = player.status === 'loading' || player.status === 'waiting';
  const expired = player.status === 'source-expired';
  const active = player.status === 'playing';
  const hasQueue = player.queue.length > 0 && player.queueIndex !== null;

  return (
    <article className="mx-auto flex min-h-svh w-full max-w-3xl flex-col bg-bg px-4 py-5 text-ink sm:px-6 lg:px-8">
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="inline-flex min-h-11 items-center gap-2 rounded-md px-2 text-sm text-ink-2 outline-none hover:bg-surface hover:text-ink focus-visible:ring-2 focus-visible:ring-accent-ring"
        >
          <ArrowLeft className="size-4" aria-hidden />
          Back
        </Button>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => {
            player.close();
            navigate('/catalog');
          }}
          className="grid size-11 place-items-center rounded-md text-ink-2 outline-none hover:bg-surface hover:text-ink focus-visible:ring-2 focus-visible:ring-accent-ring"
          aria-label="Close player"
        >
          <X className="size-5" aria-hidden />
        </Button>
      </div>

      <div className="my-auto py-12 text-center">
        <p className="font-mono text-xs uppercase tracking-[0.16em] text-accent">Now playing</p>
        <h1 className="mt-5 break-words text-4xl font-semibold tracking-[-0.04em] sm:text-6xl">
          {player.item.title}
        </h1>
        <p className="mt-4 text-lg text-ink-2">{player.item.artist ?? 'Unknown artist'}</p>
        <p className="mt-2 font-mono text-xs text-ink-3">
          {player.playbackContext?.label ?? player.source?.label ?? 'Preparing source'}
        </p>

        <div className="mx-auto mt-12 max-w-xl">
          <label className="sr-only" htmlFor="player-position">
            Playback position
          </label>
          <input
            id="player-position"
            type="range"
            min={0}
            max={Math.max(player.duration, 0)}
            step={0.1}
            value={Math.min(player.currentTime, player.duration || 0)}
            onChange={(event) => player.seek(event.currentTarget.valueAsNumber)}
            disabled={!player.duration}
            className="h-11 w-full cursor-pointer accent-accent disabled:cursor-not-allowed"
          />
          <div className="flex justify-between font-mono text-xs text-ink-3">
            <span>{formatPlaybackTime(player.currentTime)}</span>
            <span>{formatPlaybackTime(player.duration)}</span>
          </div>
        </div>

        <div className="mx-auto mt-8 grid w-full max-w-sm grid-cols-5 items-center gap-2">
          <div className="grid place-items-center">
            {hasQueue ? (
              <Button
                variant="ghost"
                size="icon"
                onClick={player.toggleShuffle}
                disabled={player.canonicalQueue.length < 2}
                aria-label={player.shuffle ? 'Turn shuffle off' : 'Turn shuffle on'}
                aria-pressed={player.shuffle}
              >
                <Shuffle className={player.shuffle ? 'size-5 text-accent' : 'size-5'} aria-hidden />
              </Button>
            ) : null}
          </div>
          <div className="grid place-items-center">
            {hasQueue ? (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => void player.previous()}
                disabled={player.queueIndex === 0 && player.currentTime <= 5}
                aria-label="Previous track"
              >
                <SkipBack className="size-5" aria-hidden />
              </Button>
            ) : null}
          </div>
          <Button
            size="icon"
            onClick={() => void (expired ? player.retry() : player.toggle())}
            className="grid size-16 justify-self-center place-items-center rounded-full bg-accent text-accent-ink outline-none hover:bg-accent-dim focus-visible:ring-2 focus-visible:ring-accent-ring focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
            aria-label={expired ? 'Refresh playback source' : active ? 'Pause' : 'Play'}
          >
            {busy ? (
              <LoaderCircle
                className="size-6 animate-spin motion-reduce:animate-none"
                aria-hidden
              />
            ) : expired ? (
              <RotateCw className="size-6" aria-hidden />
            ) : active ? (
              <Pause className="size-6" fill="currentColor" aria-hidden />
            ) : (
              <Play className="size-6" fill="currentColor" aria-hidden />
            )}
          </Button>
          <div className="grid place-items-center">
            {hasQueue ? (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => void player.next()}
                disabled={player.queueIndex === player.queue.length - 1}
                aria-label="Next track"
              >
                <SkipForward className="size-5" aria-hidden />
              </Button>
            ) : null}
          </div>
          <QuickCreateButton className="justify-self-center" />
        </div>

        {hasQueue ? (
          <p className="mt-4 font-mono text-xs text-ink-3">
            Queue · {player.queueIndex! + 1} of {player.queue.length}
          </p>
        ) : null}

        {player.error ? (
          <p className="mx-auto mt-6 max-w-md text-sm text-err" role="alert">
            {player.error}
          </p>
        ) : null}
      </div>
    </article>
  );
}
